/**
 * Statistics Dashboard Type Definitions
 * 
 * Comprehensive types for the Phase 1 statistics implementation
 * Covers all 6 widgets: Overall, Letters, Trends, Fingers, Weak Spots, Calendar
 */

// Time period utilities
export type TimePeriod = 'all' | 'month' | 'week' | 'custom';

export interface CustomPeriod {
  startDate: Date;
  endDate: Date;
}

// Main dashboard data structure
export interface DashboardStats {
  overall: OverallStats;
  letters: LetterPerformanceData[];
  dailyProgress: DailyProgressData[];
  fingerPerformance: FingerPerformanceData[];
  weakSpots: WeakSpotData[];
  calendarData: CalendarData[];
  achievements: AchievementData[];
}

// Widget 1: Overall Statistics Block
export interface OverallStats {
  timeSpent: number; // milliseconds
  totalKeystrokes: number;
  accuracy: number; // percentage
  avgSpeed: number; // letters per second
  wpm: number;
  bestWord5: number; // milliseconds for fastest 5-letter word
  trend: 'up' | 'down' | 'stable';
  sessionsCount: number;
  improvementPercent: number; // vs previous period
}

// Widget 2: Letter Performance Matrix
export interface LetterPerformanceData {
  letter: string;
  practiceCount: number;
  accuracy: number;
  avgSpeed: number; // milliseconds
  bestTime: number; // milliseconds
  trend: 'improving' | 'declining' | 'stable';
  recentAccuracy: number; // last 7 days accuracy
  fingerAssignment: string; // which finger types this letter
  difficultyScore: number; // 1-10 scale
}

// Widget 3: Daily Progress Trends
export interface DailyProgressData {
  date: string; // YYYY-MM-DD format
  accuracy: number;
  speed: number; // letters per second
  wpm: number;
  sessionsCount: number;
  totalKeystrokes: number;
  totalDuration: number; // milliseconds
  averageSessionQuality: number; // 1-10 composite score
}

// Widget 4: Finger Performance Analysis
export interface FingerPerformanceData {
  finger: string; // 'Pinky', 'Ring', 'Middle', 'Index', 'Thumb'
  hand: 'left' | 'right';
  accuracy: number;
  avgSpeed: number; // milliseconds
  assignedLetters: string[];
  practiceCount: number;
  strengthScore: number; // 1-10 relative performance
  improvementRate: number; // percentage improvement over time
}

// Widget 5: Weak Spots Detector
export interface WeakSpotData {
  letter: string;
  accuracy: number;
  avgSpeed: number;
  practiceCount: number;
  improvementPotential: number; // 0-100 score
  recommendation: string;
  priority: 'high' | 'medium' | 'low';
  estimatedPracticeTime: number; // minutes to improve
}

// Widget 6: Training Calendar Heatmap
export interface CalendarData {
  date: string; // YYYY-MM-DD
  intensity: number; // 0-4 scale (GitHub style)
  sessionsCount: number;
  totalDuration: number; // milliseconds
  accuracy: number;
  qualityScore: number; // 1-10 composite performance
  streak: boolean; // part of current streak
}

// Achievement system (for gamification)
export interface AchievementData {
  id: string;
  title: string;
  description: string;
  achieved: boolean;
  progress: number; // 0-100
  category: 'speed' | 'accuracy' | 'consistency' | 'milestone';
  icon: string; // emoji or icon name
  unlockedDate?: Date;
}

// Chart data formats for Recharts
export interface ChartDataPoint {
  date: string;
  accuracy: number;
  speed: number;
  wpm: number;
  sessions: number;
}

export interface LetterChartData {
  letter: string;
  accuracy: number;
  speed: number;
  practiceCount: number;
  trend: number; // numerical trend for sorting
}

// Filter and sort options
export interface FilterOptions {
  period: TimePeriod;
  customPeriod?: CustomPeriod;
  trainingType?: 'defTraining' | 'customTraining' | 'all';
  minAccuracy?: number;
  sortBy?: 'accuracy' | 'speed' | 'practiceCount' | 'trend';
  sortDirection?: 'asc' | 'desc';
}

// View mode options
export type ViewMode = 'chart' | 'table' | 'grid';

// Statistics computation helpers
export interface StatisticsMetrics {
  mean: number;
  median: number;
  standardDeviation: number;
  percentile95: number;
  trendCoefficient: number; // linear regression slope
}

// Finger mapping configuration
export interface FingerMapping {
  [finger: string]: {
    hand: 'left' | 'right';
    letters: string[];
    strength: number; // relative strength vs other fingers
  };
}

// Performance thresholds for categorization
export interface PerformanceThresholds {
  accuracy: {
    excellent: number; // >= 98%
    good: number; // >= 95%
    average: number; // >= 90%
    needsWork: number; // < 90%
  };
  speed: {
    fast: number; // <= 150ms
    average: number; // <= 250ms
    slow: number; // > 250ms
  };
  wpm: {
    expert: number; // >= 60
    advanced: number; // >= 40
    intermediate: number; // >= 25
    beginner: number; // < 25
  };
}

// Error handling
export interface StatisticsError {
  code: 'NO_DATA' | 'FETCH_ERROR' | 'CALCULATION_ERROR' | 'INVALID_PERIOD';
  message: string;
  details?: any;
}

// Loading states
export interface LoadingState {
  overall: boolean;
  letters: boolean;
  trends: boolean;
  fingers: boolean;
  weakSpots: boolean;
  calendar: boolean;
}

// Default performance thresholds
export const DEFAULT_THRESHOLDS: PerformanceThresholds = {
  accuracy: {
    excellent: 98,
    good: 95,
    average: 90,
    needsWork: 90
  },
  speed: {
    fast: 150,
    average: 250,
    slow: 250
  },
  wpm: {
    expert: 60,
    advanced: 40,
    intermediate: 25,
    beginner: 25
  }
};

// Finger mapping configuration
export const FINGER_MAPPING: FingerMapping = {
  leftPinky: {
    hand: 'left',
    letters: ['Q', 'A', 'Z', '1', '!'],
    strength: 0.6
  },
  leftRing: {
    hand: 'left', 
    letters: ['W', 'S', 'X', '2', '@'],
    strength: 0.7
  },
  leftMiddle: {
    hand: 'left',
    letters: ['E', 'D', 'C', '3', '#'],
    strength: 0.9
  },
  leftIndex: {
    hand: 'left',
    letters: ['R', 'T', 'F', 'G', 'V', 'B', '4', '5', '$', '%'],
    strength: 1.0
  },
  leftThumb: {
    hand: 'left',
    letters: [' '],
    strength: 0.8
  },
  rightThumb: {
    hand: 'right',
    letters: [' '],
    strength: 0.8
  },
  rightIndex: {
    hand: 'right',
    letters: ['Y', 'U', 'H', 'J', 'N', 'M', '6', '7', '^', '&'],
    strength: 1.0
  },
  rightMiddle: {
    hand: 'right',
    letters: ['I', 'K', ',', '8', '*'],
    strength: 0.9
  },
  rightRing: {
    hand: 'right',
    letters: ['O', 'L', '.', '9', '('],
    strength: 0.7
  },
  rightPinky: {
    hand: 'right',
    letters: ['P', '[', ']', ';', "'", '/', '0', ')', '-', '=', '\\'],
    strength: 0.6
  }
};

// Color themes for visualization
export interface StatisticsTheme {
  primary: string;
  success: string;
  warning: string;
  danger: string;
  neutral: string;
  background: string;
  cardBg: string;
  text: string;
  border: string;
}

export const STATISTICS_THEME: StatisticsTheme = {
  primary: '#3B82F6',      // Blue - main charts
  success: '#10B981',      // Green - improvements/good performance
  warning: '#F59E0B',      // Orange - needs attention
  danger: '#EF4444',       // Red - problem areas
  neutral: '#6B7280',      // Gray - secondary info
  background: '#F8FAFC',   // Light background
  cardBg: '#FFFFFF',       // Card backgrounds
  text: '#1F2937',         // Primary text
  border: '#E5E7EB'        // Borders and dividers
};

// Export commonly used types as union
export type TrendDirection = 'improving' | 'declining' | 'stable';
export type PriorityLevel = 'high' | 'medium' | 'low';
export type PerformanceCategory = 'excellent' | 'good' | 'average' | 'needsWork';
